import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Button,
  Box,
  Container,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  LinearProgress,
  Divider,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Pagination,
  Select,
  FormControl,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  InputAdornment,
  IconButton,
  Stack,
  Autocomplete,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff,
  ExpandMore,
  ExpandLess,
  Delete,
} from '@mui/icons-material';
import LockResetIcon from '@mui/icons-material/LockReset';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import NoEncryptionIcon from '@mui/icons-material/NoEncryption';
import { useNavigate } from 'react-router-dom';
import { getStoredToken, authenticatedFetch, updateAdminStatus, updateIntrusionStatus, updateSuperAdminStatus, getUsers, getAllUsernames, registerUser } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from './PageHeader';
import { formatDate } from '../utils/dateUtils';

const PRIMARY = '#40518A';

const PasswordRequirements = ({ password }) => {
  const requirements = [
    { label: 'It must be at least 8 characters.', valid: password.length >= 8 },
    { label: 'At least one number to your password.', valid: /\d/.test(password) },
    { label: 'At least one special character (e.g., ! @ # $ %)', valid: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  return (
    <Box sx={{ mt: 1, mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
      <Typography variant="subtitle2" gutterBottom>
        Password Requirements:
      </Typography>
      {requirements.map((req, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {req.valid ? (
            <CheckCircle fontSize="small" color="success" />
          ) : (
            <Cancel fontSize="small" color="error" />
          )}
          <Typography variant="body2" color={req.valid ? 'text.primary' : 'text.secondary'}>
            {req.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const UserManagement = () => {
  const navigate = useNavigate();
  const { user: authUser, refreshUser, isSuperAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    has_next: false,
    has_prev: false,
    total_pages: 0,
    current_page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogType, setDialogType] = useState(''); // 'add', 'edit', 'reset-password'
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    new_password: '',
    is_admin: false,
    roleLevel: '', // '' | 'admin' | 'super_admin' (super admin Add User radios)
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [showPassword, setShowPassword] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [allUsernames, setAllUsernames] = useState([]);
  const [superAdminCount, setSuperAdminCount] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [userManageMenu, setUserManageMenu] = useState({ anchorEl: null, user: null });
  const [formErrors, setFormErrors] = useState({
    username: '',
    email: '',
    password: '',
  });

  const closeUserManageMenu = () => setUserManageMenu({ anchorEl: null, user: null });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const isCurrentUserId = (userId) =>
    authUser != null &&
    userId != null &&
    String(authUser.user_id) === String(userId);

  const reloadEntirePage = () => {
    window.location.reload();
  };

  const handlePageChange = (event, page) => {
    const newOffset = (page - 1) * pagination.limit;
    loadUsers(pagination.limit, newOffset, userQuery, showInactive);
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    loadUsers(newLimit, 0, userQuery, showInactive);
  };

  const loadUsers = useCallback(async (
    limit = pagination.limit,
    offset = pagination.offset,
    query = '',
    inactive = false,
  ) => {
    setLoading(true);
    try {
      const data = await getUsers(limit, offset, query || '', !inactive);
      console.log('Users API response:', data);

      // Handle new paginated response format
      if (data.users && data.pagination) {
        setUsers(data.users);
        setPagination(data.pagination);
        if (typeof data.super_admin_count === 'number') {
          setSuperAdminCount(data.super_admin_count);
        }
      } else {
        // Fallback for old format
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showSnackbar('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.offset]);

  const loadAllUsernames = useCallback(async (inactive = false) => {
    try {
      const data = await getAllUsernames('', !inactive);
      setAllUsernames(Array.isArray(data?.names) ? data.names : []);
    } catch (error) {
      console.error('Error loading usernames for autocomplete:', error);
      setAllUsernames([]);
    }
  }, []);

  const handleStatusFilterChange = (event, newValue) => {
    if (newValue == null) return;
    const inactive = newValue === 'deactive';
    setShowInactive(inactive);
    // Explicit params prevent the mount effect from re-fetching when state updates.
    loadUsers(pagination.limit, 0, userQuery, inactive);
    loadAllUsernames(inactive);
  };

  useEffect(() => {
    // Check if user is authenticated
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return;
    }

    // Initial load only. Tab / search / pagination handlers own subsequent fetches.
    loadUsers(pagination.limit, 0, '', false);
    loadAllUsernames(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleOpenDialog = (type, userData = null) => {
    setDialogType(type);
    setSelectedUser(userData);
    setFormData({
      username: userData?.username || '',
      email: userData?.email || '',
      password: '',
      new_password: '',
      is_admin: userData?.is_admin || false,
      roleLevel: '',
    });
    setFormErrors({ username: '', email: '', password: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({ username: '', email: '', password: '', new_password: '', is_admin: false, roleLevel: '' });
    setFormErrors({ username: '', email: '', password: '' });
    setShowPassword(false);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateAddUserForm = () => {
    const errors = {};

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    }

    setFormErrors({
      username: errors.username || '',
      email: errors.email || '',
      password: errors.password || '',
    });

    return Object.keys(errors).length === 0;
  };

  const handleAddUser = async () => {
    if (!validateAddUserForm()) {
      return;
    }

    const applyApiErrorToForm = (errorMessage) => {
      const message = errorMessage || 'Failed to create user';
      const lowerMessage = message.toLowerCase();

      if (lowerMessage.includes('username')) {
        setFormErrors((prev) => ({ ...prev, username: message }));
        return true;
      }
      if (lowerMessage.includes('email')) {
        setFormErrors((prev) => ({ ...prev, email: message }));
        return true;
      }
      return false;
    };

    try {
      const isSuperAdminRole = isSuperAdmin && formData.roleLevel === 'super_admin';
      const isAdminRole = isSuperAdmin
        ? (formData.roleLevel === 'admin' || formData.roleLevel === 'super_admin')
        : formData.is_admin;

      const response = await registerUser({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        is_admin: Boolean(isAdminRole),
        is_super_admin: Boolean(isSuperAdminRole),
      });

      if (response.ok) {
        showSnackbar('User created successfully', 'success');
        loadUsers(pagination.limit, pagination.offset, userQuery, showInactive);
        loadAllUsernames(showInactive);
        handleCloseDialog();
      } else {
        const errorData = await response.json();
        let errorMessage = 'Failed to create user';

        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Handle Pydantic validation errors
            const firstError = errorData.detail[0];
            if (firstError.msg) {
              errorMessage = firstError.msg.replace('Value error, ', '');
            }
          } else if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        }

        if (!applyApiErrorToForm(errorMessage)) {
          showSnackbar(errorMessage, 'error');
        }
      }
    } catch (error) {
      const errorMessage = error?.message || 'Failed to create user';
      if (!applyApiErrorToForm(errorMessage)) {
        showSnackbar(errorMessage, 'error');
      }
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      const response = await authenticatedFetch(`/auth/admin/users/${userId}/activate`, {
        method: 'POST',
      });

      if (response.ok) {
        showSnackbar('User activated successfully', 'success');
        await loadUsers(pagination.limit, pagination.offset, userQuery, showInactive);
        await loadAllUsernames(showInactive);
      } else {
        showSnackbar('Failed to activate user', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to activate user', 'error');
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      const response = await authenticatedFetch(`/auth/admin/users/${userId}/deactivate`, {
        method: 'POST',
      });

      if (response.ok) {
        showSnackbar('User deactivated successfully', 'success');
        await loadUsers(pagination.limit, pagination.offset, userQuery, showInactive);
        await loadAllUsernames(showInactive);
      } else {
        showSnackbar('Failed to deactivate user', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to deactivate user', 'error');
    }
  };

  const handleToggleUserActiveStatus = (user) => {
    const isCurrentlyActive = user.is_active;
    const title = isCurrentlyActive ? 'Deactivate User' : 'Activate User';
    const message = isCurrentlyActive
      ? `Are you sure you want to deactivate ${user.username}?`
      : `Are you sure you want to activate ${user.username}?`;

    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm: () => (
        isCurrentlyActive
          ? handleDeactivateUser(user.user_id)
          : handleActivateUser(user.user_id)
      ),
    });
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !canResetPassword(selectedUser)) {
      showSnackbar('You do not have permission to reset this user\'s password', 'error');
      return;
    }
    try {
      const response = await authenticatedFetch(`/auth/admin/users/${selectedUser.user_id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({
          new_password: formData.new_password,
        }),
      });

      if (response.ok) {
        showSnackbar('Password reset successfully', 'success');
        handleCloseDialog();
      } else {
        let errorMessage = 'Failed to reset password';
        try {
          const errorData = await response.json();
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // use default message
        }
        showSnackbar(errorMessage, 'error');
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to reset password', 'error');
    }
  };

  const handleToggleAdminStatus = (user) => {
    if (user.is_super_admin) {
      return;
    }
    const title = user.is_admin ? 'Demote User' : 'Promote to Admin';
    const message = user.is_admin
      ? `Are you sure you want to remove admin privileges from ${user.username}?`
      : `Are you sure you want to promote ${user.username} to admin?`;

    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm: () => updateUserAdminStatus(user.user_id, !user.is_admin),
    });
  };

  const updateUserAdminStatus = async (userId, isAdmin) => {
    try {
      const response = await updateAdminStatus(userId, isAdmin);

      if (response.ok) {
        const action = isAdmin ? 'promoted' : 'demoted';
        showSnackbar(`User ${action} successfully`, 'success');
        if (isCurrentUserId(userId)) {
          try {
            await refreshUser();
          } catch (_) {
            reloadEntirePage();
            return;
          }
        }
        loadUsers(pagination.limit, pagination.offset, userQuery, showInactive);
      } else {
        showSnackbar('Failed to update admin status', 'error');
      }
    } catch (error) {
      showSnackbar('Failed to update admin status', 'error');
    }
  };

  const handleToggleSuperAdminStatus = (user, promote) => {
    const title = promote ? 'Promote to Super Admin' : 'Demote to Admin';
    const message = promote
      ? `Are you sure you want to promote ${user.username} to super admin?`
      : `Are you sure you want to demote ${user.username} from super admin to admin?`;

    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm: () => updateUserSuperAdminStatus(user.user_id, promote),
    });
  };

  const updateUserSuperAdminStatus = async (userId, isSuperAdmin) => {
    try {
      const response = await updateSuperAdminStatus(userId, isSuperAdmin);

      if (response.ok) {
        let successMessage = isSuperAdmin
          ? 'User promoted to super admin successfully'
          : 'User demoted to admin successfully';
        try {
          const responseData = await response.json();
          if (responseData.message) {
            successMessage = responseData.message;
          }
        } catch (e) {
          // use default message
        }
        showSnackbar(successMessage, 'success');
        loadUsers(pagination.limit, pagination.offset, userQuery, showInactive);
      } else {
        let errorMessage = 'Failed to update super admin status';
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // use default message
        }
        showSnackbar(errorMessage, 'error');
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to update super admin status', 'error');
    }
  };

  const getRoleLabel = (user) => {
    if (user.is_super_admin) return 'Super Admin';
    if (user.is_admin) return 'Admin';
    return 'User';
  };

  const getRoleStyles = (user) => {
    if (user.is_super_admin) {
      return { bgcolor: '#fce7f3', color: '#9d174d' };
    }
    if (user.is_admin) {
      return { bgcolor: '#e0e7ff', color: '#3730a3' };
    }
    return { bgcolor: '#e5e7eb', color: '#374151' };
  };

  const canPromoteToSuperAdmin = (user) =>
    isSuperAdmin
    && !isCurrentUserId(user.user_id)
    && user.is_admin
    && !user.is_super_admin;

  const canDemoteFromSuperAdmin = (user) =>
    isSuperAdmin
    && !isCurrentUserId(user.user_id)
    && user.is_super_admin
    && superAdminCount > 1;

  const canToggleAdminStatus = (user) => !user.is_super_admin;

  // Super admin may reset only their own password. Admin passwords require a super admin actor.
  const canResetPassword = (user) => {
    if (!user) return false;
    if (user.is_super_admin) {
      return isSuperAdmin && isCurrentUserId(user.user_id);
    }
    if (user.is_admin && !isSuperAdmin) return false;
    return true;
  };

  const formatLastLogin = (value) => (value ? formatDate(value) : 'Never');

  const renderUserManageActions = (user) => {
    const menuOpen = Boolean(
      userManageMenu.anchorEl
      && userManageMenu.user
      && userManageMenu.user.user_id === user.user_id
    );

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          onClick={(e) => {
            setUserManageMenu((prev) =>
              prev.anchorEl && prev.user?.user_id === user.user_id
                ? { anchorEl: null, user: null }
                : { anchorEl: e.currentTarget, user }
            );
          }}
          sx={{
            textTransform: 'none',
            borderColor: PRIMARY,
            color: PRIMARY,
            whiteSpace: 'nowrap',
            '&:hover': { borderColor: PRIMARY, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
          }}
        >
          Manage
        </Button>
        <Menu
          anchorEl={userManageMenu.anchorEl}
          open={menuOpen}
          onClose={closeUserManageMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 240 } }}
        >
          {canResetPassword(user) && (
            <MenuItem
              onClick={() => {
                closeUserManageMenu();
                handleOpenDialog('reset-password', user);
              }}
            >
              <ListItemIcon>
                <LockResetIcon fontSize="small" sx={{ color: PRIMARY }} />
              </ListItemIcon>
              <ListItemText primary="Reset password" />
            </MenuItem>
          )}

          {canToggleAdminStatus(user) && (
            <MenuItem
              onClick={() => {
                closeUserManageMenu();
                handleToggleAdminStatus(user);
              }}
            >
              <ListItemIcon>
                {user.is_admin ? (
                  <ArrowDownwardIcon fontSize="small" sx={{ color: '#ed6c02' }} />
                ) : (
                  <ArrowUpwardIcon fontSize="small" sx={{ color: PRIMARY }} />
                )}
              </ListItemIcon>
              <ListItemText primary={user.is_admin ? 'Demote to user' : 'Promote to admin'} />
            </MenuItem>
          )}

          {canPromoteToSuperAdmin(user) && (
            <MenuItem
              onClick={() => {
                closeUserManageMenu();
                handleToggleSuperAdminStatus(user, true);
              }}
            >
              <ListItemIcon>
                <ArrowUpwardIcon fontSize="small" sx={{ color: '#9c27b0' }} />
              </ListItemIcon>
              <ListItemText primary="Promote to super admin" />
            </MenuItem>
          )}

          {canDemoteFromSuperAdmin(user) && (
            <MenuItem
              onClick={() => {
                closeUserManageMenu();
                handleToggleSuperAdminStatus(user, false);
              }}
            >
              <ListItemIcon>
                <ArrowDownwardIcon fontSize="small" sx={{ color: '#ed6c02' }} />
              </ListItemIcon>
              <ListItemText primary="Demote to admin" />
            </MenuItem>
          )}

          <Divider sx={{ my: 0.5 }} />

          <MenuItem
            onClick={() => {
              closeUserManageMenu();
              handleToggleUserActiveStatus(user);
            }}
          >
            <ListItemIcon>
              {user.is_active ? (
                <PersonOffIcon fontSize="small" sx={{ color: '#d32f2f' }} />
              ) : (
                <PersonIcon fontSize="small" sx={{ color: '#2e7d32' }} />
              )}
            </ListItemIcon>
            <ListItemText primary={user.is_active ? 'Deactivate user' : 'Activate user'} />
          </MenuItem>

          <MenuItem
            onClick={() => {
              closeUserManageMenu();
              handleToggleIntrusionStatus(user);
            }}
          >
            <ListItemIcon>
              {user.is_intrusion ? (
                <NoEncryptionIcon fontSize="small" sx={{ color: '#ed6c02' }} />
              ) : (
                <SecurityIcon fontSize="small" sx={{ color: PRIMARY }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={user.is_intrusion ? 'Disable intrusion access' : 'Enable intrusion access'}
            />
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  const handleToggleIntrusionStatus = (user) => {
    const title = user.is_intrusion ? 'Disable Intrusion Access' : 'Enable Intrusion Access';
    const message = user.is_intrusion
      ? `Are you sure you want to disable intrusion access for ${user.username}?`
      : `Are you sure you want to enable intrusion access for ${user.username}?`;

    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm: () => updateUserIntrusionStatus(user.user_id, !user.is_intrusion),
    });
  };

  const updateUserIntrusionStatus = async (userId, isIntrusion) => {
    try {
      const response = await updateIntrusionStatus(userId, isIntrusion);

      if (response.ok) {
        // Try to get the actual message from the response
        let successMessage = `User intrusion access ${isIntrusion ? 'enabled' : 'disabled'} successfully`;
        try {
          const responseData = await response.json();
          if (responseData.message) {
            successMessage = responseData.message;
          }
        } catch (e) {
          // If parsing fails, use default message
        }
        showSnackbar(successMessage, 'success');
        if (isCurrentUserId(userId)) {
          try {
            await refreshUser();
          } catch (_) {
            reloadEntirePage();
            return;
          }
        }
        loadUsers(pagination.limit, pagination.offset, userQuery, showInactive);
      } else {
        // Try to get error message from response
        let errorMessage = 'Failed to update intrusion status';
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch (e) {
          // If parsing fails, use default message
        }
        showSnackbar(errorMessage, 'error');
        // Still reload to sync UI with server state
        loadUsers(pagination.limit, pagination.offset, userQuery, showInactive);
      }
    } catch (error) {
      showSnackbar(error.message || 'Failed to update intrusion status', 'error');
      // Reload to sync UI with server state
      loadUsers(pagination.limit, pagination.offset, userQuery, showInactive);
    }
  };

  const userNameOptions = allUsernames;

  const renderDialog = () => {
    const isAddUser = dialogType === 'add';
    const isResetPassword = dialogType === 'reset-password';

    return (
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isAddUser ? 'Add New User' : isResetPassword ? 'Reset Password' : 'Edit User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {isAddUser && (
              <>
                <TextField
                  fullWidth
                  required
                  label="Username"
                  value={formData.username}
                  onChange={(e) => handleFormChange('username', e.target.value)}
                  margin="normal"
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                />
                <TextField
                  fullWidth
                  required
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  margin="normal"
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                />
                <TextField
                  fullWidth
                  required
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                  margin="normal"
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <PasswordRequirements password={formData.password} />
                {isSuperAdmin ? (
                  <FormControl component="fieldset" sx={{ mt: 1.5 }}>
                    <FormLabel component="legend" sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
                      Role
                    </FormLabel>
                    <RadioGroup
                      row
                      value={formData.roleLevel}
                      onChange={(e) => handleFormChange('roleLevel', e.target.value)}
                    >
                      <FormControlLabel value="admin" control={<Radio color="primary" />} label="Admin" />
                      <FormControlLabel value="super_admin" control={<Radio color="primary" />} label="Super Admin" />
                    </RadioGroup>
                  </FormControl>
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.is_admin}
                        onChange={(e) => handleFormChange('is_admin', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Admin User"
                  />
                )}
              </>
            )}
            {isResetPassword && (
              <>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={(e) => handleFormChange('new_password', e.target.value)}
                  margin="normal"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <PasswordRequirements password={formData.new_password} />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={isAddUser ? handleAddUser : handleResetPassword}
            variant="contained"
          >
            {isAddUser ? 'Create User' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box>
      <Container maxWidth="lg">
        <PageHeader
          title="User Management"
          subtitle="Manage system users and their permissions"
        />

        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              User Accounts
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', flex: 1, alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Autocomplete
                freeSolo
                size="small"
                options={userNameOptions}
                value={userQuery || null}
                inputValue={userQuery || ''}
                onInputChange={(event, newInputValue, reason) => {
                  setUserQuery(newInputValue || '');
                  if (reason === 'reset') return;
                }}
                onChange={(event, newValue) => {
                  const query = typeof newValue === 'string' ? newValue : '';
                  setUserQuery(query);
                  loadUsers(pagination.limit, 0, query, showInactive);
                }}
                sx={{ minWidth: { xs: '100%', sm: 220 }, width: { xs: '100%', sm: 'auto' } }}
                renderInput={(params) => (
                  <Tooltip
                    title={userQuery || ''}
                    arrow
                    placement="bottom"
                    PopperProps={{ disablePortal: true }}
                    disableHoverListener={!userQuery}
                  >
                    <TextField
                      {...params}
                      placeholder="Search by Name"
                      variant="outlined"
                      size="small"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          const query = e.currentTarget.value || '';
                          setUserQuery(query);
                          loadUsers(pagination.limit, 0, query, showInactive);
                        }
                      }}
                    />
                  </Tooltip>
                )}
              />
              <ToggleButtonGroup
                value={showInactive ? 'deactive' : 'active'}
                exclusive
                onChange={handleStatusFilterChange}
                size="small"
                sx={{
                  height: 40,
                  '& .MuiToggleButton-root': {
                    minHeight: 40,
                    px: 1.5,
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <ToggleButton value="active">
                  <Visibility sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                  Active
                </ToggleButton>
                <ToggleButton value="deactive">
                  <Delete sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                  Deactive
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
     
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenDialog('add')}
                fullWidth={isMobile}
              >
                Add User
              </Button>
            </Stack>
          </Box>

          {loading ? (
            <LinearProgress />
          ) : users.length > 0 ? (
            isMobile ? (
              <Stack spacing={1.5} sx={{ p: 2 }}>
                {users.map((user) => {
                  const roleStyles = getRoleStyles(user);
                  const intrusionStyles = user.is_intrusion
                    ? { bgcolor: '#fff7ed', color: '#9a3412' }
                    : { bgcolor: '#e5e7eb', color: '#374151' };

                  return (
                    <Card key={user.user_id} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                            {user.username}
                          </Typography>
                        </Stack>

                        <Stack spacing={0.75}>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Email:</strong> {user.email}
                          </Typography>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Created By:</strong> {user.created_by_name || user.created_by || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(user.created_at)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Updated By:</strong> {user.updated_by_name || user.updated_by || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(user.updated_at)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Role:</strong>{' '}
                            <Chip size="small" label={getRoleLabel(user)} sx={{ ...roleStyles, fontWeight: 600 }} />
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Intrusion Access:</strong>{' '}
                            <Chip size="small" label={user.is_intrusion ? 'Active' : 'Inactive'} sx={{ ...intrusionStyles, fontWeight: 600 }} />
                          </Typography>
                          {/* <Typography variant="body2" color="text.secondary">
                            <strong>Login Count:</strong> {user.login_count || 0}
                          </Typography> */}
                          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            <strong>Last Login:</strong> {formatLastLogin(user.last_login)}
                          </Typography>
                        </Stack>

                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.4, mb: 1, display: 'block' }}>
                            Action
                          </Typography>
                          {renderUserManageActions(user)}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 860 }}>
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, maxWidth: 220 }}>Username</TableCell>
                      {/* <TableCell sx={{ fontWeight: 600 }}>Email</TableCell> */}
                      <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Created By</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Updated By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Intrusion Access</TableCell>
                      {/* <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Login Count</TableCell> */}
                      <TableCell sx={{ fontWeight: 600, minWidth: 180, whiteSpace: 'nowrap' }}>Last Login</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: 110, whiteSpace: 'nowrap' }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => {
                      const roleStyles = getRoleStyles(user);
                      const intrusionStyles = user.is_intrusion
                        ? { bgcolor: '#fff7ed', color: '#9a3412' }
                        : { bgcolor: '#e5e7eb', color: '#374151' };

                      return (
                        <TableRow key={user.user_id} hover>
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Stack spacing={0.25}>
                              <Typography sx={{ fontWeight: 600 }}>{user.username}</Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontWeight: 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                }}
                              >
                                {user.email}
                              </Typography>
                            </Stack>
                          </TableCell>
                          {/* <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell> */}
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {user.created_by_name || user.created_by || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(user.created_at)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {user.updated_by_name || user.updated_by || '-'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(user.updated_at)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={getRoleLabel(user)}
                              sx={{ ...roleStyles, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={user.is_intrusion ? 'Active' : 'Inactive'}
                              sx={{ ...intrusionStyles, fontWeight: 600 }}
                            />
                          </TableCell>
                          {/* <TableCell>{user.login_count || 0}</TableCell> */}
                          <TableCell sx={{ minWidth: 180, whiteSpace: 'nowrap' }}>
                            <Tooltip title={formatLastLogin(user.last_login)} arrow placement="top">
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {formatLastLogin(user.last_login)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center" sx={{ width: 110, whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                            {renderUserManageActions(user)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No users found. Add a user to get started.
            </Typography>
          )}
        </Card>

        {/* Pagination Controls */}
        {!loading && pagination.total > 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, flexWrap: 'wrap', gap: 2 }}>
              {/* Items per page selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Items per page:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={pagination.limit}
                    onChange={handleLimitChange}
                    displayEmpty
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Pagination info */}
              <Typography variant="body2" color="text.secondary">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} users
              </Typography>

              {/* Pagination component */}
              <Pagination
                count={pagination.total_pages}
                page={pagination.current_page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        )}
      </Container>

      {/* Dialog */}
      {renderDialog()}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              confirmDialog.onConfirm();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
